// /client/src/components/VerifyEmail.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying...');
  const [userInfo, setUserInfo] = useState(null);

  // Check if this is a password reset flow
  const mode = searchParams.get('mode');
  const isResetMode = mode === 'reset';

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid link. No token provided.');
        return;
      }

      try {
        // Use different endpoint based on mode
        const endpoint = isResetMode 
          ? `/api/contacts/verify-reset-token/${token}`
          : `/api/contacts/verify-email/${token}`;

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage(isResetMode ? 'Reset link verified!' : 'Email verified successfully!');
          setUserInfo(data);
          
          // Redirect to password setup after 2 seconds
          setTimeout(() => {
            if (isResetMode) {
              navigate(`/set-password?token=${token}&mode=reset`);
            } else {
              navigate(`/set-password?userId=${data.userId}`);
            }
          }, 2000);
        } else {
          setStatus('error');
          setMessage(data.error || 'Failed to verify. The link may be invalid or expired.');
        }
      } catch (error) {
        console.error('Error verifying:', error);
        setStatus('error');
        setMessage('Network error. Please try again later.');
      }
    };

    verifyToken();
  }, [searchParams, navigate, isResetMode]);

  const title = isResetMode ? 'Password Reset' : 'Email Verification';

  return (
    <section className="hero is-fullheight theme-light" data-theme="light">
      <div className="hero-body">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-5-tablet is-4-desktop">
              <div className="box">
                <div className="has-text-centered mb-5">
                  <h1 className="title is-3">{title}</h1>
                </div>

                {status === 'verifying' && (
                  <div className="has-text-centered">
                    <div className="loader-wrapper">
                      <div className="loader is-loading"></div>
                    </div>
                    <p className="mt-4">{message}</p>
                  </div>
                )}

                {status === 'success' && (
                  <div className="notification is-success is-light">
                    <p className="has-text-centered">
                      <span className="icon is-large">
                        <i className="fas fa-check-circle fa-2x"></i>
                      </span>
                    </p>
                    <p className="has-text-centered mt-3">
                      <strong>{message}</strong>
                    </p>
                    {userInfo && (
                      <p className="has-text-centered mt-2">
                        {isResetMode 
                          ? 'Redirecting to set your new password...'
                          : `Welcome, ${userInfo.firstName}! Redirecting to password setup...`
                        }
                      </p>
                    )}
                  </div>
                )}

                {status === 'error' && (
                  <div>
                    <div className="notification is-danger is-light">
                      <p className="has-text-centered">
                        <span className="icon is-large">
                          <i className="fas fa-exclamation-circle fa-2x"></i>
                        </span>
                      </p>
                      <p className="has-text-centered mt-3">
                        <strong>{message}</strong>
                      </p>
                    </div>
                    <div className="has-text-centered mt-4">
                      {isResetMode ? (
                        <button 
                          className="button is-primary"
                          onClick={() => navigate('/reset-password')}
                        >
                          Request New Reset Link
                        </button>
                      ) : (
                        <button 
                          className="button is-primary"
                          onClick={() => navigate('/login')}
                        >
                          Return to Login
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerifyEmail;